import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Switch,
    CircularProgress,
    Box,
    Grid,
    Tooltip,
    IconButton,
    Paper,
    Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import {
    getPlanByIdAction,
    createPlanAction,
    updatePlanAction,
} from "../../Actions/PlanManagementActions";
import { MyInfoIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import CloseIcon from "@mui/icons-material/Close";

const { VALIDATION } = FRONTEND_MESSAGES;

const validationSchema = Yup.object({
    name: Yup.string()
        .trim()
        .matches(/^[a-zA-Z\s]+$/, VALIDATION.INVALID_FORMAT("Plan name"))
        .max(50, VALIDATION.MAX_LENGTH("Plan name", 50))
        .required(VALIDATION.REQUIRED_FIELD("Plan name")),
    description: Yup.string()
        .trim()
        .matches(/^[a-zA-Z\s]+$/, VALIDATION.INVALID_FORMAT("Description"))
        .max(500, VALIDATION.MAX_LENGTH("Description", 500))
        .required(VALIDATION.REQUIRED_FIELD("Description")),
    monthly_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Monthly price"))
        .positive(VALIDATION.POSITIVE_ONLY("Monthly price"))
        .max(999999999, VALIDATION.MAX_VALUE("Monthly price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Monthly price")),
    yearly_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Yearly price"))
        .positive(VALIDATION.POSITIVE_ONLY("Yearly price"))
        .max(999999999, VALIDATION.MAX_VALUE("Yearly price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Yearly price")),
    user_limit_type: Yup.string().oneOf(["0", "1"]).required(),
    user_limit_value: Yup.number().when("user_limit_type", {
        is: "0",
        then: (schema) =>
            schema
                .typeError(VALIDATION.NUMERIC_ONLY("User limit"))
                .integer(VALIDATION.NO_DECIMAL("User limit"))
                .min(1, VALIDATION.MIN_VALUE("User limit", 1))
                .max(9999, VALIDATION.MAX_VALUE("User limit", 9999))
                .required(VALIDATION.REQUIRED_FIELD("User limit")),
        otherwise: (schema) => schema.notRequired(),
    }),
    message_limit_type: Yup.string().oneOf(["0", "1"]).required(),
    message_limit_value: Yup.number().when("message_limit_type", {
        is: "0",
        then: (schema) =>
            schema
                .typeError(VALIDATION.NUMERIC_ONLY("Message limit"))
                .integer(VALIDATION.NO_DECIMAL("Message limit"))
                .min(1, VALIDATION.MIN_VALUE("Message limit", 1))
                .max(100000, VALIDATION.MAX_VALUE("Message limit", 100000))
                .required(VALIDATION.REQUIRED_FIELD("Message limit")),
        otherwise: (schema) => schema.notRequired(),
    }),
    agent_training_limit_type: Yup.string().oneOf(["0", "1"]),
    agent_training_limit_value: Yup.number().when("agent_training_limit_type", {
        is: "0",
        then: (schema) =>
            schema
                .typeError(VALIDATION.NUMERIC_ONLY("Agent training limit"))
                .integer(VALIDATION.NO_DECIMAL("Agent training limit"))
                .min(1, VALIDATION.MIN_VALUE("Agent training limit", 1))
                .max(10000, VALIDATION.MAX_VALUE("Agent training limit", 10000))
                .required(VALIDATION.REQUIRED_FIELD("Agent training limit")),
        otherwise: (schema) => schema.notRequired(),
    }),
    isActive: Yup.boolean(),
});


// Helper: Compare old vs new values
const getChangedPlanFields = (oldData, newData) => {
    const changes = {};
    if (!oldData) return changes;

    Object.keys(newData).forEach((key) => {
        const oldVal = oldData[key];
        const newVal = newData[key];

        // Handle null/undefined/empty cases
        const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
        const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

        if (oldIsEmpty && newIsEmpty) return;

        // Deep comparison
        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changes[key] = {
                old: oldVal,
                new: newVal,
            };
        }
    });

    return changes;
};

const AddEditPlan = ({
    open,
    handleClose,
    id,
    refetchPlanList = () => { },
    handleToast = () => { },
    changePortalFilter,
}) => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [planData, setPlanData] = useState(null);
    const [changedFields, setChangedFields] = useState({});

    const formik = useFormik({
        initialValues: {
            name: "",
            description: "",
            monthly_price: "",
            yearly_price: "",
            user_limit_type: "0",
            user_limit_value: 1,
            message_limit_type: "0",
            message_limit_value: 1,
            agent_training_limit_type: "0",
            agent_training_limit_value: 1,
            isActive: true,
        },
        validationSchema,
        onSubmit: async (values) => {
            values = { ...values, name: values.name?.trim(), description: values.description?.trim() };
            setLoading(true);
            try {
                if (id) {
                    const dataToSend = {
                        ...values,
                        changedFields: JSON.stringify(Object.keys(changedFields)),
                        comparisonData: JSON.stringify(changedFields),
                    };
                    const response = await updatePlanAction(id, dataToSend);
                    const { message } = response?.data || {};
                    handleToast("success", message);
                } else {
                    const response = await createPlanAction(values);
                    const { message } = response?.data || {};
                    changePortalFilter("qbot");
                    handleToast("success", message);
                }
                handleClose();
            } catch (err) {
                const message = err?.message ?? (id ? "Something went wrong while updating plan" : "Something went wrong while adding plan");
                handleToast("error", message);
                console.error("❌ Error saving plan:", err);
            } finally {
                setLoading(false);
                refetchPlanList();
            }
        },
    });

    useEffect(() => {
        const fetchPlan = async () => {
            if (!id) return;
            setFetching(true);
            try {
                const response = await getPlanByIdAction(id);
                const { data } = response?.data || {};

                setPlanData(data);

                formik.resetForm({
                    values: {
                        name: data.name || "",
                        description: data.description || "",
                        monthly_price: data.monthly_price ?? "",
                        yearly_price: data.yearly_price ?? "",
                        user_limit_type:
                            data.user_limit_type !== undefined
                                ? String(data.user_limit_type)
                                : "0",
                        user_limit_value: data.user_limit_value ?? 1,
                        message_limit_type:
                            data.message_limit_type !== undefined
                                ? String(data.message_limit_type)
                                : "0",
                        message_limit_value: data.message_limit_value ?? "",
                        agent_training_limit_type:
                            data.agent_training_limit_type !== undefined
                                ? String(data.agent_training_limit_type)
                                : "0",
                        agent_training_limit_value:
                            data.agent_training_limit_value ?? "",
                        isActive: data.isActive ?? true,
                    },
                });
            } catch (err) {
                console.error("❌ Error fetching plan:", err);
            } finally {
                setFetching(false);
            }
        };
        fetchPlan();
    }, [id]);


    // ✅ NEW: Track changed fields
    useEffect(() => {
        if (!id || !planData) return;

        const oldData = {
            name: planData.name || "",
            description: planData.description || "",
            monthly_price: planData.monthly_price ?? "",
            yearly_price: planData.yearly_price ?? "",
            user_limit_type: String(planData.user_limit_type ?? "0"),
            user_limit_value: planData.user_limit_value ?? 1,
            message_limit_type: String(planData.message_limit_type ?? "0"),
            message_limit_value: planData.message_limit_value ?? "",
            agent_training_limit_type: String(planData.agent_training_limit_type ?? "0"),
            agent_training_limit_value: planData.agent_training_limit_value ?? "",
            isActive: planData.isActive ?? true,
        };

        const changed = getChangedPlanFields(oldData, formik.values);
        setChangedFields(changed);
    }, [formik.values, planData, id]);

    const handleTextChange = (e) => {
        formik.handleChange(e);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleNumberChange = (e) => {
        const value = e.target.value.replace(/\s/g, '');
        formik.setFieldValue(e.target.name, value);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const SectionHeader = ({ title }) => (
        <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "primary.main", mb: 2 }}
        >
            {title}
        </Typography>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                className="text-bg-primary"
                sx={{
                    py: 2.5,
                    px: 3,
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    position: "relative",
                }}
            >
                {id ? "Update Qbot Plan" : "Create New Qbot Plan"}
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: "#fff",
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {fetching ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={400}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <form onSubmit={formik.handleSubmit}>
                    <DialogContent sx={{ px: 3, py: 3 }}>
                        {/* Plan Information Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 3,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                            }}
                        >
                            <SectionHeader title="Plan Information" />
                            <Grid container spacing={2.5}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>Plan Name<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter plan name"
                                        name="name"
                                        size="small"
                                        fullWidth
                                        disabled={id}
                                        value={formik.values.name}
                                        onChange={handleTextChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.name &&
                                            Boolean(formik.errors.name)
                                        }
                                        helperText={
                                            formik.touched.name &&
                                            formik.errors.name
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>Description<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter description"
                                        name="description"
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={formik.values.description}
                                        onChange={handleTextChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.description &&
                                            Boolean(formik.errors.description)
                                        }
                                        helperText={
                                            formik.touched.description &&
                                            formik.errors.description
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Pricing Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 3,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                            }}
                        >
                            <SectionHeader title="Pricing" />
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Monthly Price<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter monthly price"
                                        name="monthly_price"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={formik.values.monthly_price}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.monthly_price &&
                                            Boolean(formik.errors.monthly_price)
                                        }
                                        helperText={
                                            formik.touched.monthly_price &&
                                            formik.errors.monthly_price
                                        }
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        mr: 1,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    CI$
                                                </Box>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Yearly Price<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter yearly price"
                                        name="yearly_price"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={formik.values.yearly_price}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.yearly_price &&
                                            Boolean(formik.errors.yearly_price)
                                        }
                                        helperText={
                                            formik.touched.yearly_price &&
                                            formik.errors.yearly_price
                                        }
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        mr: 1,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    CI$
                                                </Box>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Limits Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 3,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                            }}
                        >
                            <SectionHeader title="Usage Limits" />

                            {/* User Limit */}
                            <Box mb={3}>
                                <FormControl fullWidth>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: formik.values.user_limit_type === "0"
                                                ? "1fr 1fr"
                                                : "1fr",
                                            gap: 2,
                                            alignItems: "end",
                                        }}
                                    >
                                        {/* Left Column */}
                                        <Box>
                                            <FormLabel
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    mb: 1,
                                                    fontSize: "0.875rem",
                                                    fontWeight: 500,
                                                    color: "text.primary",
                                                }}
                                            >
                                                User Limit
                                                <Tooltip
                                                    title="Set the maximum number of users allowed for this plan"
                                                    arrow
                                                    placement="right"
                                                >
                                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                                        <MyInfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </FormLabel>
                                            <RadioGroup
                                                row
                                                name="user_limit_type"
                                                value={formik.values.user_limit_type}
                                                onChange={formik.handleChange}
                                            >
                                                <FormControlLabel
                                                    value="0"
                                                    control={<Radio size="small" />}
                                                    label="Limited"
                                                />
                                                <FormControlLabel
                                                    value="1"
                                                    control={<Radio size="small" />}
                                                    label="Unlimited"
                                                />
                                            </RadioGroup>
                                        </Box>

                                        {/* Right Column - Only show when Limited */}
                                        {formik.values.user_limit_type === "0" && (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    Limit Value<span className="text-danger ms-1">*</span>
                                                </Typography>
                                                <TextField
                                                    name="user_limit_value"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    placeholder="Enter user limit"
                                                    value={formik.values.user_limit_value}
                                                    onChange={handleNumberChange}
                                                    onBlur={formik.handleBlur}
                                                    error={
                                                        formik.touched.user_limit_value &&
                                                        Boolean(formik.errors.user_limit_value)
                                                    }
                                                    helperText={
                                                        formik.touched.user_limit_value &&
                                                        formik.errors.user_limit_value
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </FormControl>
                            </Box>

                            {/* Message Limit */}
                            <Box mb={3}>
                                <FormControl fullWidth>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: formik.values.message_limit_type === "0"
                                                ? "1fr 1fr"
                                                : "1fr",
                                            gap: 2,
                                            alignItems: "end",
                                        }}
                                    >
                                        {/* Left Column */}
                                        <Box>
                                            <FormLabel
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    mb: 1,
                                                    fontSize: "0.875rem",
                                                    fontWeight: 500,
                                                    color: "text.primary",
                                                }}
                                            >
                                                Message Limit
                                                <Tooltip
                                                    title="Your message limit will reset every month"
                                                    arrow
                                                    placement="right"
                                                >
                                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                                        <MyInfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </FormLabel>
                                            <RadioGroup
                                                row
                                                name="message_limit_type"
                                                value={formik.values.message_limit_type}
                                                onChange={formik.handleChange}
                                            >
                                                <FormControlLabel
                                                    value="0"
                                                    control={<Radio size="small" />}
                                                    label="Limited"
                                                />
                                                <FormControlLabel
                                                    value="1"
                                                    control={<Radio size="small" />}
                                                    label="Unlimited"
                                                />
                                            </RadioGroup>
                                        </Box>

                                        {/* Right Column - Only show when Limited */}
                                        {formik.values.message_limit_type === "0" && (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    Limit Value<span className="text-danger ms-1">*</span>
                                                </Typography>
                                                <TextField
                                                    name="message_limit_value"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    placeholder="Enter message limit"
                                                    value={formik.values.message_limit_value}
                                                    onChange={handleNumberChange}
                                                    onBlur={formik.handleBlur}
                                                    error={
                                                        formik.touched.message_limit_value &&
                                                        Boolean(formik.errors.message_limit_value)
                                                    }
                                                    helperText={
                                                        formik.touched.message_limit_value &&
                                                        formik.errors.message_limit_value
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </FormControl>
                            </Box>

                            {/* Agent Training Limit */}
                            <Box>
                                <FormControl fullWidth>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: formik.values.agent_training_limit_type === "0"
                                                ? "1fr 1fr"
                                                : "1fr",
                                            gap: 2,
                                            alignItems: "end",
                                        }}
                                    >
                                        {/* Left Column */}
                                        <Box>
                                            <FormLabel
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    mb: 1,
                                                    fontSize: "0.875rem",
                                                    fontWeight: 500,
                                                    color: "text.primary",
                                                }}
                                            >
                                                Agent Training Limit
                                                <Tooltip
                                                    title="Your agent training limit will reset every month"
                                                    arrow
                                                    placement="right"
                                                >
                                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                                        <MyInfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </FormLabel>
                                            <RadioGroup
                                                row
                                                name="agent_training_limit_type"
                                                value={formik.values.agent_training_limit_type}
                                                onChange={formik.handleChange}
                                            >
                                                <FormControlLabel
                                                    value="0"
                                                    control={<Radio size="small" />}
                                                    label="Limited"
                                                />
                                                <FormControlLabel
                                                    value="1"
                                                    control={<Radio size="small" />}
                                                    label="Unlimited"
                                                />
                                            </RadioGroup>
                                        </Box>

                                        {/* Right Column - Only show when Limited */}
                                        {formik.values.agent_training_limit_type === "0" && (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    Limit Value<span className="text-danger ms-1">*</span>
                                                </Typography>
                                                <TextField
                                                    name="agent_training_limit_value"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    placeholder="Enter training limit"
                                                    value={formik.values.agent_training_limit_value}
                                                    onChange={handleNumberChange}
                                                    onBlur={formik.handleBlur}
                                                    error={
                                                        formik.touched.agent_training_limit_value &&
                                                        Boolean(formik.errors.agent_training_limit_value)
                                                    }
                                                    helperText={
                                                        formik.touched.agent_training_limit_value &&
                                                        formik.errors.agent_training_limit_value
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </FormControl>
                            </Box>
                        </Paper>

                        {/* Status Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                            }}
                        >
                            <SectionHeader title="Plan Status" />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formik.values.isActive}
                                        onChange={(e) =>
                                            formik.setFieldValue(
                                                "isActive",
                                                e.target.checked
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            fontWeight={500}
                                        >
                                            {formik.values.isActive
                                                ? "Active"
                                                : "Inactive"}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {formik.values.isActive
                                                ? "This plan is available for users to subscribe"
                                                : "This plan is hidden from users"}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                    </DialogContent>
                    <DialogActions className="border-top p-7 d-flex justify-content-between">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="btn btn-sm btn-outline btn-outline-primary"
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="btn btn-sm btn-primary"
                        >
                            {id ? "Update" : "Create"} Plan
                            {loading && (
                                <span
                                    className="spinner-border spinner-border-sm ms-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                            )}
                        </button>
                    </DialogActions>
                </form>
            )}
        </Dialog>
    );
};

export default AddEditPlan;
